/**
 * O vocabulário das escritas que a IA do módulo faz.
 *
 * São DUAS, e nenhuma delas grava sem o clique do consultor: adicionar clientes
 * ao funil da migração (spec-001 do `project-onboarding-with-ai-fee-fixo`) e
 * registrar o DESFECHO dela — ganho ou perdido (spec-002). As duas seguem o
 * mesmo protocolo two-step: `confirmar=false` devolve o preview e não grava
 * nada; `confirmar=true` grava, com changelog, na mesma transação.
 *
 * Por que este arquivo existe, em vez de os nomes viverem só em
 * `api/apiFeeFixoIa.js`: eles têm DOIS leitores. O captador roteia o
 * `tool_result` por eles, e o card (`fee-fixo-guia-confirmacao`) escolhe a copy
 * por eles — e o card é apresentação pura, que não deve arrastar o cliente SSE
 * (e a store, e o axios) para dentro do próprio import só para ler duas
 * strings. Constante com dois leitores mora em `constants/`, como manda o
 * CLAUDE.md deste módulo.
 *
 * ★ Cada nome aqui é ESPELHO do servidor, e o casamento é o que faz o card
 * abrir e a confirmação ser interceptada:
 *
 * | daqui                        | de lá                                                |
 * | ---------------------------- | ---------------------------------------------------- |
 * | `TOOL_MIGRACAO_EM_LOTE`      | `protocolo_confirmacao_migracao.NOME_DA_TOOL`        |
 * | `OPERACAO_MIGRACAO_EM_LOTE`  | `migracao_em_lote.OPERACAO`                          |
 * | `TOOL_DESFECHO_MIGRACAO`     | `protocolo_confirmacao_desfecho.NOME_DA_TOOL`        |
 * | `OPERACAO_DESFECHO_MIGRACAO` | `desfecho_migracao.OPERACAO`                         |
 * | `ACOES_DO_ITEM_*`            | `migracao_em_lote.ACAO_*` / `desfecho_pedido.ACAO_*` |
 *
 * Um nome trocado de um lado só não quebra nada visivelmente: o `tool_result`
 * chega sem dono, nenhum card abre e a conversa segue como se o preview nunca
 * tivesse existido. `apiFeeFixoIa.unit.js` trava os quatro contra o valor
 * transcrito do backend.
 */

// ── A escrita da spec-001: clientes entram no funil da migração ─────────────

/** Nome da tool no vocabulário do chat — é por ele que o `tool_result` é reconhecido. */
export const TOOL_MIGRACAO_EM_LOTE = 'adicionar_clientes_migracao_fee_fixo'

/** Marca da operação DENTRO do `value` — é por ela que a tela sabe QUAL card montar. */
export const OPERACAO_MIGRACAO_EM_LOTE = 'adicionar_clientes_migracao'

/** O cliente que já tem conversa aberta é PULADO no commit, nunca duplicado. */
export const ACAO_JA_NO_FUNIL = 'ja_no_funil'

/**
 * As duas ações que o motor marca por item. Ação fora desta lista não
 * atravessa: um rótulo desconhecido viraria aviso mudo (ou pior, um aviso
 * inventado) na linha do cliente.
 */
export const ACOES_DO_ITEM_MIGRACAO = ['criar_oportunidade', ACAO_JA_NO_FUNIL]

// ── A escrita da spec-002: o desfecho da migração ───────────────────────────

export const TOOL_DESFECHO_MIGRACAO = 'registrar_desfecho_migracao_fee_fixo'

export const OPERACAO_DESFECHO_MIGRACAO = 'registrar_desfecho_migracao'

/**
 * As duas ações do desfecho, no vocabulário do card (`desfecho_pedido.ACAO_*`).
 * A peneira é POR OPERAÇÃO, e não uma lista só com as quatro: um `ja_no_funil`
 * chegando no card do desfecho acenderia o aviso "vou pular", que não existe
 * nesta escrita — ela FECHA uma conversa, não pula nenhuma.
 */
export const ACOES_DO_ITEM_DESFECHO = ['marcar_ganho', 'marcar_perdido']

// ── A escrita da spec-007: a simulação criada pela conversa ─────────────────

/**
 * Nome da tool no vocabulário do chat.
 *
 * ★ Espelho dos DOIS lados desde a [T105] (24/08/2026):
 * `tool_fee_fixo.py:1108` (`TOOL_SCHEMA['name']`) e `simulacao_chat.py:74`
 * (`OPERACAO`). A versão anterior desta nota dizia que a tool ainda não existia
 * no backend — envelheceu no mesmo dia, e fica registrado porque nota de
 * transição que sobrevive à transição vira afirmação falsa.
 *
 * ★ **E a interceptação determinística EXISTE desde a [T113]** — esta nota
 * dizia o contrário, com ênfase, e é a segunda vez que ela envelhece no mesmo
 * lugar. Medido em 24/08/2026: `PADRAO_POR_OPERACAO`
 * (`core/protocolo_confirmacao_migracao.py:115`) tem **três** entradas, e a
 * terceira é `OPERACAO_SIMULACAO: PADRAO_DA_CONFIRMACAO_SIMULACAO`, com módulo
 * próprio (`protocolo_confirmacao_simulacao`).
 *
 * O que motivava a dúvida foi resolvido por outro caminho, e não pelo texto: o
 * commit exige `resultados`, que nascem no NAVEGADOR e nenhuma frase carrega —
 * então eles viajam ANTES, pelo depósito
 * (`api/apiFeeFixoSimulacaoDeposito.js`), e a confirmação continua sendo TEXTO.
 * É por isso que a ordem em `view-fee-fixo-guia-simulacao.js` é contrato:
 * depositar → só com 200, mandar a frase.
 *
 * O modo de falha de um nome divergente é o de sempre nesta família: o
 * `tool_result` chega **sem dono**, nenhum card abre, e a conversa segue como
 * se o preview nunca tivesse existido — nada fica vermelho.
 */
export const TOOL_CRIAR_SIMULACAO = 'criar_simulacao_fee_fixo'

/** Marca da operação DENTRO do `value` — é por ela que a tela sabe qual card montar. */
export const OPERACAO_CRIAR_SIMULACAO = 'criar_simulacao_fee_fixo'
